/** Pending reauthentication is navigation context, never evidence of authorization. */
export interface AuthBlock {
  code?: "sso_required" | "deactivated";
  message: string;
  organization?: string;
  orgId?: string;
}

const AUTH_BLOCK_KEY = "lato.authBlockMessage";

export function validSsoOrganization(value: unknown): string | undefined {
  return typeof value === "string" && /^org_[A-Za-z0-9]{1,128}$/.test(value)
    ? value : undefined;
}

export function validOrgId(value: unknown): string | undefined {
  return typeof value === "string" && /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(value)
    ? value : undefined;
}

export function rememberAuthBlock(block: AuthBlock): void {
  try {
    sessionStorage.setItem(AUTH_BLOCK_KEY, JSON.stringify(block));
  } catch {
    // Storage can be unavailable in restricted browser environments.
  }
}

/** Keep the SSO target through failed OAuth redirects and page remounts. */
export function readAuthBlock(): AuthBlock | undefined {
  try {
    const raw = sessionStorage.getItem(AUTH_BLOCK_KEY);
    if (!raw) return undefined;
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      // Older tabs stored just the explanation, with no routing context.
      return { message: raw };
    }
    if (!parsed || typeof parsed !== "object") return undefined;
    const value = parsed as Record<string, unknown>;
    if (typeof value.message !== "string") return undefined;
    return {
      message: value.message,
      code: value.code === "sso_required" || value.code === "deactivated" ? value.code : undefined,
      organization: validSsoOrganization(value.organization),
      orgId: validOrgId(value.orgId),
    };
  } catch {
    return undefined;
  }
}

export function clearAuthBlock(): void {
  try {
    sessionStorage.removeItem(AUTH_BLOCK_KEY);
  } catch {
    // Non-fatal when browser storage is disabled.
  }
}
